import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
// Import DataGrid types explicitly
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useRBAC } from '../../context/RBACContext';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

import StatusChip, { memberStatusMap } from '../common/feedback/StatusChip';
import { Member, Role } from '../../types';
import useOrganizationMembers from '../../hooks/useOrganizationMembers';
import ErrorDisplay from '../common/feedback/ErrorDisplay';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';
import { formatDate } from '../../utils/date-formatter';
import { ApiError } from '../../utils/error-handler';

interface MembersListProps {
  organizationId: string;
  roles: Role[];
  onInvite: () => void;
}

// Helper function to determine if a user can assign a role based on permission constraints
const canAssignRole = (role: Role, userPermissions: any[] = []) => {
  console.log('MEMBERLIST: Checking if user can assign role:', role.name, role);
  console.log('MEMBERLIST: User permissions:', userPermissions);
  
  // Find the user's role assignment permissions
  const roleAssignPermission = userPermissions.find(perm => 
    (perm.resource === 'role' && perm.action === 'assign')
  );
  console.log('MEMBERLIST: Found role assign permission:', roleAssignPermission);

  // If user has system admin or organization management permissions, they can assign any role
  const hasWildcardPermissions = userPermissions.some(perm => 
    (perm.resource === 'system' && perm.action === 'admin') ||
    (perm.resource === 'organization' && (perm.action === 'update' || perm.action === 'settings'))
  );
  console.log('MEMBERLIST: Has wildcard permissions:', hasWildcardPermissions);

  // If user has org admin permissions, they can assign any role
  const hasOrgPermission = userPermissions.some(perm => 
    (perm.resource === 'organization' && (perm.action === 'update' || perm.action === 'settings')) ||
    (perm.resource === 'system' && perm.action === 'admin')
  );
  
  // Check if user has OrgAdmin role directly
  // This is a fallback check that looks at role names in the raw permissions
  const isOrgAdmin = userPermissions.some((perm: any) => {
    return perm.roleName === 'OrgAdmin' || 
           (perm.role && perm.role === 'OrgAdmin') ||
           (perm.name && perm.name.includes('OrgAdmin'));
  });
  console.log('MEMBERLIST: Is OrgAdmin directly:', isOrgAdmin);
  console.log('MEMBERLIST: Has org admin permission:', hasOrgPermission);

  if (hasWildcardPermissions || hasOrgPermission || isOrgAdmin) {
    console.log('MEMBERLIST: User has wildcard permissions or is OrgAdmin, can assign role');
    return true;
  }

  // If no role assignment permission, user can't assign any roles
  if (!roleAssignPermission) {
    console.log('MEMBERLIST: User has no role assignment permission, cannot assign any roles');
    return false;
  }

  // If the permission has role constraints, check them (using any to avoid type errors)
  const constraints = (roleAssignPermission as any).constraints;
  console.log('MEMBERLIST: Role assignment permission constraints:', constraints);
  
  if (constraints?.roles?.length) {
    const canAssign = constraints.roles.includes(role.name);
    console.log(`MEMBERLIST: Checking if role ${role.name} is in constraints.roles:`, canAssign);
    return canAssign;
  }

  // Default allow if user has role assignment permission with no constraints
  console.log('MEMBERLIST: No role constraints, allowing by default');
  return true;
};

// Row Actions Cell Component for managing per-row menu
function RowActionsCell({
  member,
  roles,
  onRoleChange,
  onRemove
}: {
  member: Member;
  roles: Role[];
  onRoleChange: (memberId: string, roleId: string) => Promise<{ success: boolean; error?: ApiError }>;
  onRemove: (memberId: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { userPermissions, permissionChecker } = useRBAC();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  // Check if this member is the current user
  // Note: User type has _id field, Member type has userId field
  const isCurrentUser = user?._id === member.userId;

  // Check if user has role:assign permission
  const hasRoleAssignPermission = permissionChecker?.hasPermission('role:assign') ?? false;

  // If user has role:assign permission, they can assign all roles
  // Otherwise, filter based on the old logic
  const assignableRoles = hasRoleAssignPermission
    ? roles
    : roles.filter(role => canAssignRole(role, userPermissions));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEditRoleOpen(false);
  };

  const handleOpenEditRoleMenu = () => {
    setEditRoleOpen(true);
  };

  const handleCloseEditRoleMenu = () => {
    setEditRoleOpen(false);
  };

  const handleRoleChange = async (roleId: string) => {
    console.log('handleRoleChange called with roleId:', roleId);

    // Find the role using _id or id, whichever is available
    const selectedRole = roles.find(r => r._id === roleId || (r as any).id === roleId);
    console.log('Selected role:', selectedRole);

    // Pass member.id (the membership ID) to the hook, which will look up the userId
    console.log('Updating role for member.id:', member.id, 'to roleId:', roleId);

    // Close the menu before making the async call
    handleClose();

    const result = await onRoleChange(member.id, roleId);
    console.log('Update result:', result);

    if (result.success) {
      const message = `${member.name}'s role has been updated to ${selectedRole?.name || 'new role'}`;
      console.log('Showing success notification:', message);
      showSuccess(message);
    } else {
      const message = result.error?.message || 'Failed to update member role';
      console.log('Showing error notification:', message);
      showError(message);
    }
  };

  const handleRemoveClick = () => {
    onRemove(member.id);
    handleClose();
  };

  // Check if user has permission to remove OTHER members
  const canRemoveOtherMembers = permissionChecker?.hasPermission('member:remove') ?? false;

  // Check if user has permission to update roles
  const canUpdateRoles = assignableRoles.length > 0;

  // Users can always leave (remove themselves), but may fail backend validation if last admin
  const canLeaveOrganization = isCurrentUser;

  // Don't render the actions button if user has no available actions
  if (!canUpdateRoles && !canRemoveOtherMembers && !canLeaveOrganization) {
    return null;
  }

  return (
    <Box>
      <IconButton
        aria-label="actions"
        aria-controls={`actions-menu-${member.id}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      {/* Main Actions Menu */}
      <Menu
        id={`actions-menu-${member.id}`}
        anchorEl={anchorEl}
        open={open && !editRoleOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canUpdateRoles && (
          <MenuItem onClick={handleOpenEditRoleMenu}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isCurrentUser ? 'Change My Role' : 'Change Role'}</ListItemText>
          </MenuItem>
        )}
        {canRemoveOtherMembers && !isCurrentUser && (
          <MenuItem
            onClick={handleRemoveClick}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Remove Member</ListItemText>
          </MenuItem>
        )}
        {canLeaveOrganization && (
          <MenuItem
            onClick={handleRemoveClick}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Leave Organization</ListItemText>
          </MenuItem>
        )}
        {!canUpdateRoles && !canRemoveOtherMembers && !canLeaveOrganization && (
          <MenuItem disabled>
            <ListItemText>No available actions</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Role Menu */}
      <Menu
        id={`role-menu-${member.id}`}
        anchorEl={anchorEl}
        open={open && editRoleOpen}
        onClose={handleCloseEditRoleMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Render only assignable roles */}
        {assignableRoles.length > 0 ? (
          assignableRoles.map((role) => (
            <MenuItem
              key={role._id}
              onClick={() => handleRoleChange(role._id)}
              selected={member.roleId === role._id}
            >
              <ListItemText>{role.name}</ListItemText>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <ListItemText>No roles available</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// Refactoring to use DataGrid with row-specific menus
const MembersList: React.FC<MembersListProps> = ({ organizationId, roles, onInvite }) => {
  const { members, isLoading, error, updateMemberRole, removeMember } = useOrganizationMembers(organizationId);
  const { showSuccess, showError } = useNotification();
  const { permissionChecker } = useRBAC();

  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Check if user has permission to invite members
  const canInviteMembers = permissionChecker?.hasPermission('member:invite') ?? false;

  const handleRemoveMember = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setMemberToRemove(member);
      setConfirmRemove(true);
    }
  };

  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      const result = await removeMember(memberToRemove.id);

      // Always close the dialog and clear state after the operation completes
      setConfirmRemove(false);
      setMemberToRemove(null);

      if (result.success) {
        showSuccess(`${memberToRemove.name} has been removed from the organization`);
      } else {
        showError(result.error?.message || 'Failed to remove member');
      }
    }
  };

  // Define columns for DataGrid
  const columns: GridColDef<Member>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<Member>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.row.avatarUrl ? (
            <Box
              component="img"
              src={params.row.avatarUrl}
              alt={params.row.name}
              sx={{ width: 32, height: 32, borderRadius: '50%', mr: 1 }}
            />
          ) : (
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          )}
          <span>{params.row.name}</span>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'roleId',
      headerName: 'Role',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<Member, string | undefined>) => {
        // Prioritize displaying the role name if available, otherwise look up by roleId
        const roleName = params.row.role || roles.find(r => r._id === params.value)?.name;
        const isRoleAdmin = roleName === 'Admin';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isRoleAdmin ? (
              <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
            ) : (
              <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            )}
            {roleName || params.value} {/* Display roleName or fallback to roleId if not found */}
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<Member, string | undefined>) => (
        <StatusChip status={params.value ?? ''} statusMap={memberStatusMap} />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      renderCell: (params: GridRenderCellParams<Member, Date | string | undefined>) => {
        let dateInput: string | undefined;
        if (params.value instanceof Date) {
          dateInput = params.value.toISOString();
        } else {
          dateInput = params.value;
        }
        return formatDate(dateInput);
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      align: 'right',
      renderCell: (params: GridRenderCellParams<Member>) => (
        <RowActionsCell 
          member={params.row}
          roles={roles}
          onRoleChange={updateMemberRole}
          onRemove={handleRemoveMember}
        />
      )
    }
  ];

  return (
    <Box sx={{ height: 'auto', width: '100%' }}>
      {error && (
        <ErrorDisplay
          error={error}
          onClose={() => {}}
          fullWidth
          sx={{ mb: 2 }}
        />
      )}

      {canInviteMembers && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onInvite}
          >
            Invite Member
          </Button>
        </Box>
      )}

      <DataGrid
        rows={members}
        columns={columns}
        loading={isLoading}
        getRowId={(row: Member) => row.id}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            overflow: 'visible',
          },
        }}
        density="standard"
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        localeText={{
          noRowsLabel: 'No members in this organization yet',
          noResultsOverlayLabel: 'No members found',
          MuiTablePagination: {
            labelRowsPerPage: 'Members per page',
          },
        }}
      />

      {/* Confirm Remove Dialog */}
      <ConfirmDialog
        open={confirmRemove}
        title="Remove Member"
        content={`Are you sure you want to remove ${memberToRemove?.name} from this organization? They will lose all access to the organization's resources.`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveMember}
        onCancel={() => {
          setConfirmRemove(false);
          setMemberToRemove(null);
        }}
        severity="error"
      />
    </Box>
  );
};

export default MembersList;
